import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { QUERY_KEYS } from "@/config";
import { fetchStakingPosition } from "@/services/real/stake.service";
import type { Address } from "@/types/web3.types";
import { useMemo } from "react";
import { useProtocolPNL } from "./useWalletAPYStore";

export interface StakingPositionStore {
    stakedBalance: string;
    rewardsEarned: string;
}

interface UseStakingPositionReturn {
    stakingPosition: StakingPositionStore;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    isSuccess: boolean;
    isPending: boolean;
}

const emptyPosition: StakingPositionStore = {
    stakedBalance: "0.00",
    rewardsEarned: "0.00",
};
export const useStakingPosition = (): UseStakingPositionReturn => {
    const { isConnected, address } = useAccount();
    const { data: pnlData } = useProtocolPNL(address);

    const {
        data: position,
        isLoading,
        isError,
        error,
        isSuccess,
        isPending,
    } = useQuery({
        queryKey: QUERY_KEYS.POSITIONS.STAKE(address),
        queryFn: async () => {
            if (!isConnected || !address) {
                throw new Error("Wallet not connected");
            }
            const stakingPosition = await fetchStakingPosition(address as Address);
            return stakingPosition;
        },
        enabled: isConnected && !!address,
    });

    const stakingPosition = useMemo(
        () =>
            position
                ? {
                      stakedBalance: position.iUSDStakedBalance,
                      rewardsEarned: `${pnlData?.stakedProfitNormalized}` ?? "0",
                  }
                : emptyPosition,
        [position, pnlData],
    );

    return {
        stakingPosition,
        isLoading,
        isError,
        error,
        isSuccess,
        isPending,
    };
};
